/**
 * Presentation helpers for pick lists — grouping, filtering, labels and the
 * colour vocabulary shared by the online screen and the printed sheet.
 *
 * Pure and clock-injected so it can be unit tested in vitest's node
 * environment, which is why the print sheet's HTML builder lives beside it
 * rather than inside a component.
 */

import type {
  PickCategory,
  PickListLine,
  PickListLineStatus,
  PickZoneGroup,
} from '@/types/pickList'
import { lotScanCode } from '@/lib/lotScanCode'

const MS_PER_DAY = 86_400_000

/** Unzoned stock collects here, always last. Mirrors the backend constant. */
export const UNASSIGNED_ZONE_CODE = 'UNASSIGNED'
export const UNASSIGNED_ZONE_NAME = 'LOCATION NOT SET'
export const UNASSIGNED_ROUTE_ORDER = 9999

/**
 * Map a free-text materialType onto a CAT pill. Mirrors the backend's
 * `pickCategory` — tenants write "Reagent", "reagents", "REAGENT", so this
 * matches on substrings rather than forcing a data migration.
 */
export function pickCategory(materialType: string | null | undefined): PickCategory {
  const t = (materialType ?? '').toLowerCase()
  if (t.includes('reagent')) return 'REAGENT'
  if (t.includes('kit')) return 'KIT'
  if (t.includes('consum')) return 'CONSUM'
  return 'OTHER'
}

export interface PillMeta {
  label: string
  /** Tailwind classes for the on-screen pill. */
  cls: string
  /** Flat colours for the print sheet, which cannot use Tailwind. */
  print: { bg: string; fg: string }
}

/**
 * CAT pill vocabulary — SOLID fills with white text, per the mockup.
 * Consumable green, reagent blue, kit purple. One definition so the printed
 * sheet and the screen cannot drift.
 */
export const CATEGORY_META: Record<PickCategory, PillMeta> = {
  CONSUM: {
    label: 'CONSUM',
    cls: 'bg-green-600 text-white border-green-600',
    print: { bg: '#16a34a', fg: '#ffffff' },
  },
  REAGENT: {
    label: 'REAGENT',
    cls: 'bg-blue-600 text-white border-blue-600',
    print: { bg: '#2563eb', fg: '#ffffff' },
  },
  KIT: {
    label: 'KIT',
    cls: 'bg-violet-600 text-white border-violet-600',
    print: { bg: '#7c3aed', fg: '#ffffff' },
  },
  OTHER: {
    label: 'OTHER',
    cls: 'bg-slate-500 text-white border-slate-500',
    print: { bg: '#64748b', fg: '#ffffff' },
  },
}

/** Line status pill vocabulary — pale fill, darker text. */
export const STATUS_META: Record<PickListLineStatus, PillMeta> = {
  OPEN: {
    label: 'OPEN',
    cls: 'bg-sky-50 text-sky-800 border-sky-200',
    print: { bg: '#f0f9ff', fg: '#075985' },
  },
  FLAGGED: {
    label: 'FLAGGED',
    cls: 'bg-amber-100 text-amber-800 border-amber-300',
    print: { bg: '#fef3c7', fg: '#92400e' },
  },
  PICKED: {
    label: 'PICKED',
    cls: 'bg-emerald-100 text-emerald-800 border-emerald-300',
    print: { bg: '#d1fae5', fg: '#065f46' },
  },
  SHORT: {
    label: 'SHORT',
    cls: 'bg-red-100 text-red-800 border-red-300',
    print: { bg: '#fee2e2', fg: '#991b1b' },
  },
}

/**
 * Expiry badge for a lot.
 *
 * Lifted out of MaterialsTable so the pick sheet, the lots table and the
 * drawer cannot drift on what counts as "near expiry". `now` is injected for
 * determinism; callers in components pass nothing and get the wall clock.
 */
export function lotExpiryStatus(
  dateStr: string | null | undefined,
  now: number = Date.now(),
): { text: string; cls: string; nearExpiry: boolean; expired: boolean } | null {
  if (!dateStr) return null
  const ms = new Date(dateStr).getTime()
  if (Number.isNaN(ms)) return null
  const days = Math.floor((ms - now) / MS_PER_DAY)
  if (days < 0) {
    return { text: 'Expired', cls: 'bg-red-100 text-red-700', nearExpiry: false, expired: true }
  }
  if (days <= 30) {
    return {
      text: `${days}d left`,
      cls: 'bg-amber-100 text-amber-700',
      nearExpiry: true,
      expired: false,
    }
  }
  return { text: 'Valid', cls: 'bg-green-100 text-green-700', nearExpiry: false, expired: false }
}

/** Group lines into zones in walk order, unzoned last. Mirrors the backend. */
/**
 * The storage unit a slot belongs to — the first segment of its path.
 *
 * One trip per freezer is the same walk a zone described, so the unit is the
 * right granularity for a pick sheet: grouping by rack would send someone back
 * to the same dewar four times.
 *
 * Mirrors `storageUnitOf` in cgt-backend's `pick-list-grouping.ts`. The page
 * RE-GROUPS after filtering rather than filtering the server's groups, so this
 * function decides what an operator actually sees — if it drifts from the
 * server's, the same sheet is filed under two different walks.
 */
export function storageUnitOf(path: string | null): string | null {
  if (!path) return null
  const unit = path.split('/')[0]?.trim()
  return unit ? unit : null
}

export function groupByZone(lines: PickListLine[]): PickZoneGroup[] {
  const groups = new Map<string, PickZoneGroup>()
  for (const line of lines) {
    const zoned = !!line.zoneCode
    // A legacy zone still wins where one exists, so nothing already grouped
    // moves. Otherwise fall back to the storage unit the slot belongs to.
    const unit = zoned ? null : storageUnitOf(line.storageNodePath ?? null)
    const zoneCode = zoned ? (line.zoneCode as string) : (unit ?? UNASSIGNED_ZONE_CODE)
    let group = groups.get(zoneCode)
    if (!group) {
      group = {
        zoneCode,
        zoneName: zoned ? (line.zoneName ?? '') : (unit ?? UNASSIGNED_ZONE_NAME),
        tempRange: zoned ? line.zoneTempRange : null,
        handlingNote: zoned ? line.zoneHandlingNote : null,
        // A hierarchy-derived group has no walk order of its own, so it sorts
        // after every real zone but ahead of genuinely unlocated stock.
        routeOrder: zoned
          ? line.zoneRouteOrder
          : unit
            ? UNASSIGNED_ROUTE_ORDER - 1
            : UNASSIGNED_ROUTE_ORDER,
        lines: [],
      }
      groups.set(zoneCode, group)
    }
    group.lines.push(line)
  }
  for (const g of groups.values()) g.lines.sort((a, b) => a.lineNo - b.lineNo)
  return [...groups.values()].sort(
    (a, b) => a.routeOrder - b.routeOrder || a.zoneCode.localeCompare(b.zoneCode),
  )
}

/** The filter tabs above the line table. */
export type PickFilterTab = 'all' | 'reagent' | 'consumable' | 'kit' | 'open' | 'exceptions'

/** A line is an exception when it carries a warning/error or is not clean. */
export function isException(line: PickListLine): boolean {
  return (
    line.status === 'FLAGGED' ||
    line.status === 'SHORT' ||
    line.alerts.some((a) => a.severity === 'warning' || a.severity === 'error')
  )
}

export function matchesTab(line: PickListLine, tab: PickFilterTab): boolean {
  switch (tab) {
    case 'reagent':
      return pickCategory(line.materialType) === 'REAGENT'
    case 'consumable':
      return pickCategory(line.materialType) === 'CONSUM'
    case 'kit':
      return pickCategory(line.materialType) === 'KIT'
    case 'open':
      return line.status === 'OPEN'
    case 'exceptions':
      return isException(line)
    default:
      return true
  }
}

/** Free-text search across the fields the mockup's placeholder promises. */
export function matchesSearch(line: PickListLine, query: string): boolean {
  const q = query.trim().toLowerCase()
  if (!q) return true
  return [
    line.materialCode,
    line.materialName,
    line.suggestedLotNumber,
    line.pickedLotNumber,
    line.locationCode,
    line.zoneCode,
    line.zoneName,
  ].some((v) => (v ?? '').toLowerCase().includes(q))
}

export function filterLines(
  lines: PickListLine[],
  tab: PickFilterTab,
  query: string,
): PickListLine[] {
  return lines.filter((l) => matchesTab(l, tab) && matchesSearch(l, query))
}

/** Counts shown on each filter tab. */
export function tabCounts(lines: PickListLine[]): Record<PickFilterTab, number> {
  return {
    all: lines.length,
    reagent: lines.filter((l) => pickCategory(l.materialType) === 'REAGENT').length,
    consumable: lines.filter((l) => pickCategory(l.materialType) === 'CONSUM').length,
    kit: lines.filter((l) => pickCategory(l.materialType) === 'KIT').length,
    open: lines.filter((l) => l.status === 'OPEN').length,
    exceptions: lines.filter(isException).length,
  }
}

/** "14 lines - 8 reagent, 4 consumable, 2 kit" — mirrors the backend label. */
export function lineCountLabel(lines: PickListLine[]): string {
  const c = tabCounts(lines)
  const other = lines.filter((l) => pickCategory(l.materialType) === 'OTHER').length
  const parts = [
    c.reagent > 0 ? `${c.reagent} reagent` : null,
    c.consumable > 0 ? `${c.consumable} consumable` : null,
    c.kit > 0 ? `${c.kit} kit` : null,
    other > 0 ? `${other} other` : null,
  ].filter((p): p is string => p !== null)
  const head = `${c.all} line${c.all === 1 ? '' : 's'}`
  return parts.length > 0 ? `${head} - ${parts.join(', ')}` : head
}

/**
 * "Due 09:00 · 2 h 45 m left", or an overdue variant.
 *
 * Returns null when nothing is due, so the caller can omit the chip entirely
 * rather than render an empty one.
 */
export function timeLeftLabel(
  requiredBy: string | null | undefined,
  now: number = Date.now(),
): { text: string; overdue: boolean } | null {
  if (!requiredBy) return null
  const due = new Date(requiredBy).getTime()
  if (Number.isNaN(due)) return null

  const time = new Date(due).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
  const diff = due - now
  if (diff <= 0) {
    return { text: `Due ${time} · overdue`, overdue: true }
  }
  const totalMinutes = Math.floor(diff / 60_000)
  const hours = Math.floor(totalMinutes / 60)
  const minutes = totalMinutes % 60
  const left = hours > 0 ? `${hours} h ${minutes} m left` : `${minutes} m left`
  return { text: `Due ${time} · ${left}`, overdue: false }
}

/** "0 of 14 lines picked". */
export function progressLabel(picked: number, total: number): string {
  return `${picked} of ${total} line${total === 1 ? '' : 's'} picked`
}

/**
 * The label a storage slot carries: `LOC-` + its path without separators,
 * upper-cased. Mirrors `cgt-backend/src/inventory/storage/slot-scan-code.ts`,
 * which the server checks transfer scans with; change both or neither.
 */
export function slotScanCode(path: string): string {
  return `LOC-${path.replace(/[^A-Za-z0-9]/g, '').toUpperCase()}`
}

/** What the two verification scans must read. Mirrors the backend helper. */
export function expectedScanCodes(line: {
  locationCode: string | null
  storageNodePath?: string | null
  materialCode: string
  suggestedLotNumber: string | null
}): { location: string | null; item: string | null } {
  // `storageNodePath ?? locationCode`, matching the backend helper AND the
  // address the desktop's location label encodes. Put-away writes a node and
  // never the legacy bin code, so reading `locationCode` first would expect a
  // null — and a null expectation is unverifiable, not a pass.
  const address = line.storageNodePath ?? line.locationCode
  const location = address ? slotScanCode(address) : null
  // Through `lotScanCode`, not inlined: the mobile lookup resolves a scanned
  // lot label with the same module's decoder, and a second copy of the format
  // here is exactly the drift that fails correctly-printed labels.
  //
  // A NAMED LOT WITH NO MATERIAL CODE MUST STILL BLOCK. `lotScanCode` answers
  // null for it — correct for an encoder, since there is no label to print —
  // but null reaches `checkScan` as "no expected value on record", which passes
  // ANY readable scan. That would turn missing master data into a SKIPPED
  // verification on a regulated pick: the sheet named a specific lot, so the
  // step has to be unsatisfiable rather than free.
  //
  // The fallback is the exact string this function produced before the encoder
  // was extracted, so the degenerate case keeps the behaviour it has always had.
  // No printed label can carry it — a real code always has a material code in
  // front of the pipe — so the step can only be failed or abandoned.
  const item =
    line.suggestedLotNumber && !line.materialCode.trim()
      ? `|LOT-${line.suggestedLotNumber}`
      : lotScanCode({
          materialCode: line.materialCode,
          lotNumber: line.suggestedLotNumber,
        })
  return { location, item }
}

/** Short date for the sheet, e.g. "30-Jun-2028". */
export function formatSheetDate(iso: string | null | undefined): string {
  if (!iso) return '—'
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return '—'
  const months = [
    'Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
    'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec',
  ]
  const day = String(d.getUTCDate()).padStart(2, '0')
  return `${day}-${months[d.getUTCMonth()]}-${d.getUTCFullYear()}`
}

/** Date + time for the header block, e.g. "03-Aug-2026 06:15". */
export function formatSheetDateTime(iso: string | null | undefined): string {
  if (!iso) return '—'
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return '—'
  const hh = String(d.getUTCHours()).padStart(2, '0')
  const mm = String(d.getUTCMinutes()).padStart(2, '0')
  return `${formatSheetDate(iso)} ${hh}:${mm}`
}

/** Quantity with its unit, e.g. "12 EA". */
export function formatQty(qty: number | null | undefined, uom: string | null): string {
  if (qty == null || Number.isNaN(qty)) return '—'
  const rounded = Math.round(qty * 1e6) / 1e6
  return uom ? `${rounded} ${uom}` : String(rounded)
}

/**
 * What to do with the text an operator typed into the Picked cell.
 *
 * Lives here, not inside the input's `onBlur`, because it is the whole rule and
 * a rule that only exists inside an event handler is a rule nobody ever tests
 * (the same reasoning as `pickSheetHtml`'s builder).
 *
 * Three outcomes, deliberately distinct:
 *  - `save`   — a new quantity to PATCH. `null` means clear the pick.
 *  - `noop`   — the value did not change, so there is nothing to send. Blur
 *               fires on every tab-through; without this each one is a request.
 *  - `revert` — the text is not a quantity. The cell restores what was stored
 *               rather than sending garbage the server would only 400.
 */
export type PickedQtyCommit =
  | { kind: 'save'; qtyPicked: number | null }
  | { kind: 'noop' }
  | { kind: 'revert' }

export function commitPickedQty(draft: string, stored: number | null): PickedQtyCommit {
  const trimmed = draft.trim()

  // Empty clears the pick. Distinct from typing 0 only in intent — the server
  // reads both as "nothing recorded" and returns the line to OPEN.
  if (trimmed === '') {
    return stored == null ? { kind: 'noop' } : { kind: 'save', qtyPicked: null }
  }

  const value = Number(trimmed)
  // `Number('')` is 0 and `Number('  ')` is 0, which is why the empty check runs
  // first; everything else that is not a finite, non-negative number is a typo.
  if (!Number.isFinite(value) || value < 0) return { kind: 'revert' }

  return value === stored ? { kind: 'noop' } : { kind: 'save', qtyPicked: value }
}

/**
 * Whether a line was picked short of what it needs.
 *
 * Mirrors the server's `derivePickLineStatus` SHORT branch. Kept as its own
 * predicate so the cell can say "short of N" before the server's status has
 * come back, without re-deriving the comparison inline.
 */
export function isShortPick(line: Pick<PickListLine, 'qtyPicked' | 'qtyRequired'>): boolean {
  return line.qtyPicked != null && line.qtyPicked > 0 && line.qtyPicked < line.qtyRequired
}

/**
 * The address to show an operator for a line.
 *
 * `locationCode` first, matching the grouping rule: the legacy zone/bin pair
 * wins wherever one exists, so nothing already on a sheet moves. Everything put
 * away since the storage hierarchy landed carries a path instead, and reading
 * only the legacy column is why such stock printed as "—".
 */
export function lineLocationLabel(
  line: Pick<PickListLine, 'locationCode' | 'storageNodePath'>,
): string | null {
  return line.locationCode ?? line.storageNodePath ?? null
}
